import {useCallback, useMemo, useState} from 'react'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import type {NudgeDefinition, NudgePlacement, NudgeContext} from '../types/nudge'
import {nudgeRegistry} from '../constants/nudges'
import {isFeatureEnabled} from '@github-ui/feature-flags'

const DISMISSED_STORAGE_KEY = 'mc_nudges_dismissed'

const DEFAULT_CONTEXT: NudgeContext = {
  hasAgentsMd: false,
  hasCreatedTask: false,
  taskCount: 0,
  isNewUser: true,
}

export function useNudge(placement: NudgePlacement, context?: Partial<NudgeContext>) {
  const [dismissedList, setDismissedList] = useLocalStorage<string[]>(DISMISSED_STORAGE_KEY, [])
  const dismissedIds = useMemo(() => new Set(dismissedList), [dismissedList])
  const [viewIndex, setViewIndex] = useState(0)

  const eligibleNudges = useMemo(() => {
    const resolvedContext: NudgeContext = {...DEFAULT_CONTEXT, ...context}
    const now = new Date()
    return nudgeRegistry
      .filter(n => n.placement === placement)
      .filter(n => !dismissedIds.has(n.id))
      .filter(n => !n.expiresAt || new Date(n.expiresAt) > now)
      .filter(n => !n.sequenceAfter || dismissedIds.has(n.sequenceAfter))
      .filter(n => !n.condition || n.condition(resolvedContext))
      .filter(n => !n.featureFlag || isFeatureEnabled(n.featureFlag as Parameters<typeof isFeatureEnabled>[0]))
      .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))
  }, [placement, dismissedIds, context])

  // Clamp the view index when the eligible list shrinks (e.g., after dismiss).
  const safeIndex = eligibleNudges.length === 0 ? 0 : Math.min(viewIndex, eligibleNudges.length - 1)
  const activeNudge = eligibleNudges[safeIndex] ?? null
  const totalCount = eligibleNudges.length
  const hasMultiple = totalCount > 1

  const goToNext = useCallback(() => {
    setViewIndex(i => (eligibleNudges.length === 0 ? 0 : (i + 1) % eligibleNudges.length))
    if (activeNudge) {
      sendEvent('agent_sessions.nudge_cycle', {
        nudge_id: activeNudge.id,
        placement,
        direction: 'next',
      })
    }
  }, [eligibleNudges.length, activeNudge, placement])

  const goToPrev = useCallback(() => {
    setViewIndex(i => {
      if (eligibleNudges.length === 0) return 0
      return (i - 1 + eligibleNudges.length) % eligibleNudges.length
    })
    if (activeNudge) {
      sendEvent('agent_sessions.nudge_cycle', {
        nudge_id: activeNudge.id,
        placement,
        direction: 'prev',
      })
    }
  }, [eligibleNudges.length, activeNudge, placement])

  const dismiss = useCallback(
    (nudge: NudgeDefinition) => {
      setDismissedList(prev => [...prev, nudge.id])
      // Reset to the top of the queue so the next highest-priority nudge shows first.
      setViewIndex(0)

      sendEvent('agent_sessions.nudge_dismiss', {
        nudge_id: nudge.id,
        placement,
        variant: nudge.variant,
      })
    },
    [placement, setDismissedList],
  )

  const dismissAll = useCallback(() => {
    const ids = eligibleNudges.map(n => n.id)
    setDismissedList(prev => [...prev, ...ids])
    setViewIndex(0)

    sendEvent('agent_sessions.nudge_dismiss_all', {
      placement,
      count: ids.length,
    })
  }, [eligibleNudges, placement, setDismissedList])

  const trackImpression = useCallback(
    (nudge: NudgeDefinition) => {
      sendEvent('agent_sessions.nudge_impression', {
        nudge_id: nudge.id,
        placement,
        variant: nudge.variant,
      })
    },
    [placement],
  )

  const trackClick = useCallback(
    (nudge: NudgeDefinition, actionLabel: string) => {
      sendEvent('agent_sessions.nudge_click', {
        nudge_id: nudge.id,
        placement,
        variant: nudge.variant,
        action_label: actionLabel,
      })
    },
    [placement],
  )

  return {
    activeNudge,
    dismiss,
    dismissAll,
    trackImpression,
    trackClick,
    goToNext,
    goToPrev,
    totalCount,
    currentIndex: safeIndex,
    hasMultiple,
  }
}
